// File: frontend/src/pages/AI1/components/ChatContainer/ChatContainer.js

import React, { useRef, useEffect, useState } from 'react';
import styles from './ChatContainer.module.css';
import MessageBubble from '../MessageBubble/MessageBubble';

const ChatContainer = ({ messages, isLoading }) => {
  const containerRef = useRef(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const lastMessageCountRef = useRef(0);
  const processedMessageIds = useRef(new Set());
  
  useEffect(() => {
    // Mark initial load as complete after first render
    setIsInitialLoad(false);
  }, []);

  // Deduplicate messages based on content
  const deduplicatedMessages = React.useMemo(() => {
    const uniqueMessages = [];
    const seenUserMessages = new Set();
    
    // First pass: collect all non-typing messages
    messages.forEach(message => {
      if (!message.isTyping) {
        if (message.role === 'user') {
          const content = Array.isArray(message.content) 
            ? message.content[0]?.text 
            : message.content;
          
          // Only add user message if we haven't seen it before
          if (!seenUserMessages.has(content)) {
            seenUserMessages.add(content);
            uniqueMessages.push(message);
          }
        } else {
          // Always add assistant messages
          uniqueMessages.push(message);
        }
      }
    });
    
    // Second pass: add typing indicators if needed
    const typingMessage = messages.find(msg => msg.isTyping);
    if (typingMessage) {
      uniqueMessages.push(typingMessage);
    }
    
    return uniqueMessages;
  }, [messages]);

  useEffect(() => {
    if (!deduplicatedMessages.length) return;
    
    const hasNewMessages = deduplicatedMessages.length > lastMessageCountRef.current;
    lastMessageCountRef.current = deduplicatedMessages.length;
    
    if (!hasNewMessages) return;
    
    const lastMessage = deduplicatedMessages[deduplicatedMessages.length - 1];
    const isUserMessage = lastMessage.role === 'user';
    const isTypingIndicator = lastMessage.isTyping;
    
    const container = containerRef.current;
    if (!container) return;
    
    if (isUserMessage) {
      // Immediately scroll to show the user message
      container.style.scrollBehavior = 'auto';
      container.scrollTop = container.scrollHeight;
    } else if (isTypingIndicator) {
      // Smooth scroll to show typing indicator
      container.style.scrollBehavior = 'smooth';
      container.scrollTop = container.scrollHeight;
    } else {
      // For AI responses, smooth scroll to bottom
      container.style.scrollBehavior = 'smooth';
      container.scrollTop = container.scrollHeight;
    }
  }, [deduplicatedMessages]);

  return (
    <div className={`${styles.container} chatContainer`} ref={containerRef}>
      <div className={styles.messagesWrapper}>
        {deduplicatedMessages.map((message, index) => {
          // Use message ID if available, otherwise create a stable key
          const messageKey = message.id || 
            `${message.role}-${index}-${message.timestamp || Date.now()}`;
          
          return (
            <div
              key={messageKey}
              className={`
                ${styles.messageWrapper}
                ${message.role === 'user' && index === deduplicatedMessages.length - 1 ? styles.fastSlideUp : ''}
              `}
            >
              <MessageBubble
                message={message}
                isUser={message.role === 'user'}
                isTyping={message.isTyping}
                isHistorical={isInitialLoad}
              />
            </div>
          );
        })}
        {isLoading && deduplicatedMessages.length === 0 && (
          <div className={styles.loadingIndicator}>
            <span className={styles.dot}></span>
            <span className={styles.dot}></span>
            <span className={styles.dot}></span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatContainer;